import { Formik } from 'formik';
import classNames from 'classnames';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import LFModal from 'components/common/LFModal/LFModal';
import Loading from 'components/common/loading/Loading';
import Input from 'components/common/inputWrapper/components/Input';

import * as toast from 'utils/toast';
import { handleError } from 'utils/errorHandler';

import { addLinkSchema } from 'schemas/addLinkSchema';
import { addQuickLinks, updateQuickLinks } from 'services/quickLinks';
import type { AddLink as addLink, QuickLinkPayload } from 'interface/link';
import { QUICK_LINKS_QUERY_KEY } from 'hooks/query/useQuickLinksQuery';

import type { LinkLibrarySelectedLink, SetHierarchyData, TriggerBtnProps } from '../types';

type QuickLinkMutationResponse = {
  message: string;
};

interface Props {
  selectedLink: LinkLibrarySelectedLink;
  setHierarchyData?: SetHierarchyData;
  isOpen?: boolean;
  onClose: () => void;
  TriggerBtn?: React.ComponentType<TriggerBtnProps>;
  isEdit: boolean;
}

const AddLink: React.FC<Props> = ({ selectedLink, isOpen, onClose, TriggerBtn, isEdit }) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues: addLink = {
    name: (isEdit && selectedLink?.name) || '',
    link: (isEdit && selectedLink?.linkUrl) || '',
  };

  const handleLink = async (values: addLink) => {
    setIsSubmitting(true);

    try {
      const categoryId = isEdit ? selectedLink.categoryId : selectedLink.id;
      const linkData: QuickLinkPayload = {
        title: values.name,
        link: values.link,
        categoryId,
      };

      const data = (
        isEdit ? await updateQuickLinks(selectedLink.id, linkData) : await addQuickLinks(linkData)
      ) as QuickLinkMutationResponse;

      toast.success({
        title: 'Success',
        message: data.message,
      });

      queryClient.invalidateQueries({ queryKey: QUICK_LINKS_QUERY_KEY });
    } catch (error) {
      handleError(error);
    } finally {
      onClose();
      setIsSubmitting(false);
    }
  };

  return (
    <LFModal
      TriggerBtn={TriggerBtn}
      isOpen={isOpen}
      onClose={onClose}
      closeButton={true}
      title={{ text: isEdit ? 'Edit link' : 'Add new link', type: 'info' }}
      className="skill-modal"
      htmlContent={
        <Formik<addLink> initialValues={initialValues} onSubmit={handleLink} validationSchema={addLinkSchema}>
          {({ handleSubmit, handleBlur, handleChange, errors, values, dirty, touched }) => {
            const nameError = touched.name && typeof errors.name === 'string' ? errors.name : undefined;
            const linkError = touched.link && typeof errors.link === 'string' ? errors.link : undefined;

            return (
              <form onSubmit={handleSubmit} className="skill-modal__form">
                <div className="skill-modal__form-body">
                  <Input
                    isMandatory
                    label="Link Name"
                    name="name"
                    placeholder="Write the name of the link"
                    className="wp-100 linkLibrary__form-title"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={nameError}
                    disabled={isSubmitting}
                    multiline={false}
                    type="text"
                  />
                  <Input
                    isMandatory
                    label="URL to the link"
                    name="link"
                    placeholder="URL link here"
                    className="wp-100 linkLibrary__form-title"
                    value={values.link}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={linkError}
                    disabled={isSubmitting}
                    multiline={false}
                    type="url"
                  />
                </div>
                <div className="skill-modal__footer">
                  <button
                    type="submit"
                    onClick={() => handleSubmit()}
                    className={classNames('btn skill-modal__btn', {
                      'btn--primary': !isSubmitting && dirty,
                      'btn--disabled': isSubmitting || !dirty,
                    })}
                    disabled={isSubmitting || !dirty}
                  >
                    <span className={classNames({ invisible: isSubmitting })}>
                      {isEdit ? 'Edit & close' : 'Add & close'}
                    </span>
                    {isSubmitting && <Loading />}
                  </button>

                  <button
                    type="button"
                    className={classNames('btn ml-10 skill-modal__btn', {
                      'btn--outlined-grey': !isSubmitting,
                      'btn--disabled': isSubmitting,
                    })}
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            );
          }}
        </Formik>
      }
    />
  );
};

export default AddLink;
